import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import { CircleAlert, Newspaper } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DataTable } from '@/components/ui/data-table'
import { ArticleCell } from './SemanticArticleResults'
import {
  KeywordArticlesApi,
  type KeywordArticle,
} from '@/services/targeting/targetingLookupApi'
import { formatPiped, type PipedValues, type Targeting } from '@/services/targeting/targeting'
import { formatCount } from '@/utils/format'

const PAGE_SIZE = 25

/**
 * How many of an article's own keywords are shown before the rest are folded
 * away. The ones the target matched on are always shown — they are the reason
 * the article is in the list.
 */
const KEYWORD_LIMIT = 40

/** An article's keywords: what it matched on first, then what else it carries. */
function KeywordList({ article }: { article: KeywordArticle }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const matched = article.matched ?? []
  const rest = article.keywords ?? []
  const shown = expanded ? rest : rest.slice(0, KEYWORD_LIMIT)
  const hidden = rest.length - shown.length

  return (
    <div className="flex flex-wrap items-center gap-1 whitespace-normal">
      {matched.map((keyword) => (
        <Badge key={keyword} variant="success">
          {keyword}
        </Badge>
      ))}
      {shown.map((keyword) => (
        <Badge key={keyword} variant="outline" className="font-normal">
          {keyword}
        </Badge>
      ))}
      {hidden > 0 && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-xs"
          onClick={() => setExpanded(true)}
        >
          {t('targeting.keywordArticles.showMore', { count: hidden })}
        </Button>
      )}
    </div>
  )
}

/**
 * The articles a keyword target currently matches — the old UI's keyword
 * preview, so a set of keywords can be tried out before it is saved.
 *
 * The check is asked for rather than run as the keywords are typed: it is a
 * query over the whole content index, narrowed only by the rest of the
 * targeting.
 */
export function KeywordArticlePreview({
  values,
  targeting,
}: {
  /** The keyword lists as they stand in the editor above. */
  values: PipedValues
  /** The targeting being edited — it narrows the search to the sites it reaches. */
  targeting: Targeting
}) {
  const { t } = useTranslation()
  const [checked, setChecked] = useState(false)

  const keywords = formatPiped(values)
  const hasKeywords = keywords.length > 0

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ['keyword-articles', keywords, targeting],
    queryFn: () => KeywordArticlesApi.search(targeting, keywords, 1, PAGE_SIZE),
    enabled: checked && hasKeywords,
    staleTime: 60 * 1000,
  })

  const columns: ColumnDef<KeywordArticle, unknown>[] = [
    {
      id: 'article',
      header: t('targeting.keywordArticles.article'),
      meta: { minWidth: 320 },
      cell: ({ row }) => <ArticleCell article={row.original} />,
    },
    {
      id: 'matched',
      header: t('targeting.keywordArticles.matched'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <KeywordList article={row.original} />,
    },
    {
      id: 'impressions',
      header: t('targeting.keywordArticles.impressions'),
      size: 120,
      meta: { align: 'right' },
      cell: ({ row }) =>
        typeof row.original.availableImpressionsLastDay === 'number'
          ? formatCount(row.original.availableImpressionsLastDay)
          : '—',
    },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Newspaper className="size-4" />
          {t('targeting.keywordArticles.title')}
        </CardTitle>
        <CardAction>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasKeywords}
            loading={isFetching}
            onClick={() => (checked ? refetch() : setChecked(true))}
          >
            {t('targeting.keywordArticles.check')}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          {hasKeywords
            ? t('targeting.keywordArticles.description')
            : t('targeting.keywordArticles.needsKeywords')}
        </p>

        {error && (
          <p className="flex items-center gap-2 text-sm text-destructive">
            <CircleAlert className="size-4" />
            {t('targeting.keywordArticles.failed')}
          </p>
        )}

        {checked && hasKeywords && !error && (
          <DataTable
            columns={columns}
            data={data?.results ?? []}
            loading={isFetching}
            emptyMessage={t('targeting.keywordArticles.noArticles')}
            columnsKey="keywordArticles"
          />
        )}
      </CardContent>
    </Card>
  )
}
